import React, { useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  List,
  Modal,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import api from '../../config/api';
import { RetainIQCallLogDetail, type RetainIQLogDetail } from './RetainIQCallLogDetail';
import {
  RETAIN_IQ_DATE_FORMAT,
  RETAIN_IQ_STORAGE_DATE_FORMAT,
  formatRetainIQDateTime,
} from './retainiqDates';

const { TextArea } = Input;
const { Text } = Typography;

export type FollowUpRow = {
  id: string;
  business_name?: string;
  agent_name?: string;
  followup_date: string;
  followup_action?: string;
  call_type?: string;
  days_until?: number;
  followup_status?: string;
  followup_acknowledged_at?: string | null;
  followup_acknowledged_by_name?: string | null;
  followup_notes_log?: { at: string; by_name?: string; text: string }[];
  ticket_number?: string | null;
};

type Props = {
  open: boolean;
  followUp: FollowUpRow | null;
  onClose: () => void;
  onUpdated?: (row: FollowUpRow) => void;
  onOpenTicket?: (n: string) => void;
};

export const RetainIQFollowUpManageModal: React.FC<Props> = ({ open, followUp, onClose, onUpdated, onOpenTicket }) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [showCallLog, setShowCallLog] = useState(false);

  const { data: logData, isLoading: logLoading } = useQuery(
    ['retain-iq-log', followUp?.id],
    () => api.get(`/api/retain-iq/logs/${followUp!.id}`).then((r) => r.data.log as RetainIQLogDetail),
    { enabled: open && !!followUp?.id && showCallLog }
  );

  useEffect(() => {
    if (!open || !followUp) return;
    form.setFieldsValue({
      followup_date: followUp.followup_date ? dayjs(followUp.followup_date) : null,
      followup_action: followUp.followup_action || '',
      note: '',
    });
    setShowCallLog(false);
  }, [open, followUp, form]);

  const patchMutation = useMutation(
    async (body: Record<string, unknown>) => {
      const res = await api.patch(`/api/retain-iq/logs/${followUp!.id}/follow-up`, body);
      return { data: res.data, body };
    },
    {
      onSuccess: ({ data, body }) => {
        message.success('Follow-up updated');
        queryClient.invalidateQueries('retain-iq-follow-ups');
        queryClient.invalidateQueries('retain-iq-reports');
        form.setFieldValue('note', '');
        const log = data?.log;
        if (log?.payload && followUp && onUpdated) {
          const p = log.payload as Record<string, unknown>;
          onUpdated({
            ...followUp,
            followup_date: String(p.followup_date || followUp.followup_date),
            followup_action: String(p.followup_action ?? followUp.followup_action ?? ''),
            followup_status: p.followup_status != null ? String(p.followup_status) : followUp.followup_status,
            followup_acknowledged_at: p.followup_acknowledged_at != null ? String(p.followup_acknowledged_at) : null,
            followup_acknowledged_by_name:
              p.followup_acknowledged_by_name != null ? String(p.followup_acknowledged_by_name) : null,
            followup_notes_log: Array.isArray(p.followup_notes_log)
              ? (p.followup_notes_log as FollowUpRow['followup_notes_log'])
              : followUp.followup_notes_log,
          });
          form.setFieldsValue({
            followup_date: p.followup_date ? dayjs(String(p.followup_date)) : null,
            followup_action: String(p.followup_action ?? ''),
          });
        }
        if (body.complete || body.reopen) {
          onClose();
        }
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } };
        message.error(err.response?.data?.error || 'Update failed');
      },
    }
  );

  const saveFields = async () => {
    const vals = await form.validateFields();
    const body: Record<string, unknown> = {};
    if (vals.followup_date) body.followup_date = vals.followup_date.format(RETAIN_IQ_STORAGE_DATE_FORMAT);
    if (vals.followup_action !== undefined) body.followup_action = vals.followup_action;
    if (vals.note?.trim()) body.note = vals.note.trim();
    if (Object.keys(body).length === 0) {
      message.info('Nothing to save');
      return;
    }
    patchMutation.mutate(body);
  };

  if (!followUp) return null;

  const notes = Array.isArray(followUp.followup_notes_log) ? followUp.followup_notes_log : [];
  const status = followUp.followup_status || 'open';
  const isCompleted = status === 'completed';

  return (
    <Modal
      title={`Follow-up — ${followUp.business_name || 'Business'}`}
      open={open}
      onCancel={onClose}
      width={720}
      footer={
        <Space wrap>
          <Button onClick={() => setShowCallLog((v) => !v)}>{showCallLog ? 'Hide call log' : 'View call log'}</Button>
          {!isCompleted && (
            <>
              <Button
                onClick={() => patchMutation.mutate({ acknowledge: true })}
                loading={patchMutation.isLoading}
                disabled={status === 'acknowledged'}
              >
                Acknowledge
              </Button>
              <Button type="primary" onClick={saveFields} loading={patchMutation.isLoading}>
                Save changes
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => patchMutation.mutate({ complete: true })}
                loading={patchMutation.isLoading}
              >
                Mark complete
              </Button>
            </>
          )}
          {isCompleted && (
            <Button onClick={() => patchMutation.mutate({ reopen: true })} loading={patchMutation.isLoading}>
              Reopen follow-up
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
    >
      <Space style={{ marginBottom: 12 }} wrap>
        {status === 'completed' ? (
          <Tag color="green">Completed</Tag>
        ) : status === 'acknowledged' ? (
          <Tag color="blue">Acknowledged</Tag>
        ) : (
          <Tag>Open</Tag>
        )}
        <Text type="secondary">Agent: {followUp.agent_name || '—'}</Text>
        <Text type="secondary">Call type: {followUp.call_type || '—'}</Text>
        {followUp.ticket_number && onOpenTicket && (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onOpenTicket(String(followUp.ticket_number))}>
            Ticket #{followUp.ticket_number}
          </Button>
        )}
      </Space>

      {followUp.followup_acknowledged_at && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Acknowledged {formatRetainIQDateTime(followUp.followup_acknowledged_at)}
          {followUp.followup_acknowledged_by_name ? ` by ${followUp.followup_acknowledged_by_name}` : ''}
        </Text>
      )}

      <Form form={form} layout="vertical" disabled={isCompleted}>
        <Form.Item name="followup_date" label="Follow-up date" rules={[{ required: true, message: 'Date required' }]}>
          <DatePicker style={{ width: '100%' }} format={RETAIN_IQ_DATE_FORMAT} />
        </Form.Item>
        <Form.Item name="followup_action" label="Action / owner">
          <Input placeholder="What needs to happen next" />
        </Form.Item>
        <Form.Item name="note" label="Add note">
          <TextArea rows={3} placeholder="Optional note (saved to history)" />
        </Form.Item>
      </Form>

      {notes.length > 0 && (
        <>
          <Divider plain>Note history</Divider>
          <List
            size="small"
            dataSource={[...notes].reverse()}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  title={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatRetainIQDateTime(item.at)}
                      {item.by_name ? ` · ${item.by_name}` : ''}
                    </Text>
                  }
                  description={<span style={{ whiteSpace: 'pre-wrap' }}>{item.text}</span>}
                />
              </List.Item>
            )}
          />
        </>
      )}

      {showCallLog && (
        <>
          <Divider plain>Call log</Divider>
          {logLoading ? (
            <Text type="secondary">Loading…</Text>
          ) : logData ? (
            <RetainIQCallLogDetail
              log={logData}
              ticketLink={
                onOpenTicket
                  ? (n) => (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onOpenTicket(n)}>
                        #{n}
                      </Button>
                    )
                  : undefined
              }
            />
          ) : (
            <Text type="secondary">Could not load call log</Text>
          )}
        </>
      )}
    </Modal>
  );
};
